import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { getRandomColor, RegionsDrawing } from '@vlabs/monokit/shared/ui/regions-drawing';
import { isEqual, uniqueId } from 'lodash';

import { DEFAULT_FULLFRAME_ROI, DRAWING_STATUS } from '../constants';
import {
  scaleRegion,
  unscaleRegion,
  convertRegionFromPercentToAbs,
  convertRegionFromAbsToPercent,
} from '../regionConverters';
import { RegionControlPanel } from './RegionControlPanel';
import st from './RegionsEdit.module.sass';
import { RegionsEditSideMenu } from './RegionsEditSideMenu';

export function RegionsEdit({
  media = undefined,
  mediaOriginSize = undefined,
  onUpdate = undefined,
  regions = undefined,
  onChangeRotationDeg = undefined,
  rotationDeg = undefined,
  droiFormat = 'polygon',
}) {
  const canvasRef = useRef();

  const [regionsList, setRegionsList] = useState([]);
  const [activeRegion, setActiveRegion] = useState(null);
  const [drawingStatus, setDrawingStatus] = useState(DRAWING_STATUS.inactive);

  useEffect(() => {
    if (!mediaOriginSize?.width || regionsList?.length) return;

    const enrichedRegionsList = regions?.map((region) => {
      switch (region.type) {
        case 'droi': {
          // point3: прямоугольный DROI — та же нормализация, что у ROI:
          // percent-координаты приводим к абсолютным (100% → полный кадр)
          if (region.figure === 'rect') {
            const droiRegionInAbs = region.mode === 'percent'
              ? { ...region, ...convertRegionFromPercentToAbs(region, mediaOriginSize) }
              : region;

            return {
              ...droiRegionInAbs,
              id: uniqueId('droi_'),
              color: getRandomColor(),
              isVisible: true,
            };
          }

          return {
            ...region,
            id: uniqueId('droi_'),
            color: getRandomColor(),
            isVisible: true,
          };
        }
        case 'roi': {
          const roiRegionInAbs = region.mode === 'abs'
            ? region
            : { ...region, ...convertRegionFromPercentToAbs(region, mediaOriginSize) };

          return {
            ...roiRegionInAbs,
            width: isEqual(roiRegionInAbs, DEFAULT_FULLFRAME_ROI) ? mediaOriginSize.width : roiRegionInAbs.width,
            height: isEqual(roiRegionInAbs, DEFAULT_FULLFRAME_ROI) ? mediaOriginSize.height : roiRegionInAbs.height,
            id: 'roi',
            color: 'positive',
            isVisible: true,
          };
        }
        default:
          return region;
      }
    });

    setRegionsList(enrichedRegionsList);
  }, [mediaOriginSize]);

  useEffect(() => {
    if (!onUpdate || !regionsList) return;
    const clearedRegionsList = regionsList?.map((region) => {
      const clearedRegion = {
        ...region,
        ...(region.figure === 'rect' && region.mode === 'percent'
          ? convertRegionFromAbsToPercent(region, mediaOriginSize)
          : null),
      };
      delete clearedRegion.id;
      delete clearedRegion.color;

      return clearedRegion;
    });

    onUpdate(clearedRegionsList);
  }, [onUpdate, regionsList]);

  const isDrawing = useMemo(() => {
    return drawingStatus === DRAWING_STATUS.start || drawingStatus === DRAWING_STATUS.drawing;
  }, [drawingStatus]);

  const roiRegion = useMemo(() => {
    return regionsList.find((region) => region.type === 'roi');
  }, [regionsList]);

  const droiList = useMemo(() => {
    return regionsList.filter((region) => region.type === 'droi');
  }, [regionsList]);

  useEffect(() => {
    if (!mediaOriginSize?.width) return;

    const fullFrameRoi = {
      ...roiRegion,
      x: 0,
      y: 0,
      width: mediaOriginSize.height,
      height: mediaOriginSize.width,
    };

    setRegionsList([fullFrameRoi]);
    setActiveRegion(null);
  }, [rotationDeg]);

  const scale = useMemo(() => {
    if (!canvasRef.current) return undefined;
    return Math.min(
      canvasRef.current.clientWidth / mediaOriginSize.width,
      canvasRef.current.clientHeight / mediaOriginSize.height,
    );
  }, [canvasRef, mediaOriginSize]);

  const rescaledRegions = useMemo(() => {
    if (!scale || !roiRegion) return [];
    return regionsList.map((region) => scaleRegion(region, scale));
  }, [roiRegion, regionsList, scale]);

  const onChangeRegion = (changedRegion) => {
    setRegionsList((list) => list.map((region) => (region.id === changedRegion.id ? changedRegion : region)));
    if (changedRegion.id === activeRegion.id) setActiveRegion(changedRegion);
  };

  const onAddDroiRegion = () => {
    // point3: 'rect' — прямоугольная зона DROI размером с кадр (рисование не запускаем,
    // правки — перетаскиванием на холсте и через панель координат)
    let newRegion;
    if (droiFormat === 'rect') {
      newRegion = {
        x: 0,
        y: 0,
        width: mediaOriginSize?.width ?? 0,
        height: mediaOriginSize?.height ?? 0,
        mode: 'abs',
        id: uniqueId('droi_'),
        type: 'droi',
        figure: 'rect',
        color: getRandomColor(),
        isVisible: true,
      };
    } else {
      newRegion = {
        coords: [],
        mode: 'abs',
        id: uniqueId('droi_'),
        type: 'droi',
        figure: 'polygon',
        form: 'wkt',
        color: getRandomColor(),
        isVisible: true,
      };
    }

    setRegionsList((list) => [...list, newRegion]);
    setActiveRegion(newRegion);
    if (droiFormat !== 'rect') setDrawingStatus(DRAWING_STATUS.start);
  };

  const onRemoveDroiRegion = (id) => {
    if (activeRegion?.id === id) setActiveRegion(null);

    setRegionsList((list) => list.filter((region) => region.id !== id && region?.coords?.length !== 0));
    setDrawingStatus(DRAWING_STATUS.inactive);
  };

  const onChangeActiveRegion = (region) => {
    setActiveRegion(region);
    setRegionsList((list) => list.filter(($region) => $region?.coords?.length !== 0));
    setDrawingStatus(DRAWING_STATUS.inactive);
  };

  const onChangeDrawingCoords = (coords) => {
    if (drawingStatus === DRAWING_STATUS.start && coords.length) setDrawingStatus(DRAWING_STATUS.drawing);
    if (isDrawing && !coords.length) setDrawingStatus(DRAWING_STATUS.inactive);
  };

  // point3: в формате 'rect' поддерживается одно значение DROI — запрещаем добавление второй зоны
  const droiAddDisabled = droiFormat === 'rect' && droiList.length > 0;

  // FIXME Вернуться и подумать над структурой, когда весь функционал будет готов
  return (
    <div className={st.Root}>
      <RegionsEditSideMenu
        activeRegion={activeRegion}
        droiAddDisabled={droiAddDisabled}
        droiList={droiList}
        isDrawing={isDrawing}
        onAddDroiRegion={droiAddDisabled ? undefined : onAddDroiRegion}
        onChangeActiveRegion={onChangeActiveRegion}
        onChangeRegion={onChangeRegion}
        onRemoveDroiRegion={onRemoveDroiRegion}
        roiRegion={roiRegion}
      />

      <div className={st.Main}>
        <div
          className={st.Canvas}
          ref={canvasRef}
        >
          { media }

          <RegionsDrawing
            activeRegion={activeRegion}
            className={st.RegionsDrawing}
            isDrawing={isDrawing}
            mediaOriginSize={mediaOriginSize}
            onChangeActiveRegion={(region) => {
              setActiveRegion(region && { ...region, ...unscaleRegion(region, scale) });
            }}
            onChangeDrawingCoords={onChangeDrawingCoords}
            onChangeRegion={(region) => {
              onChangeRegion({ ...region, ...unscaleRegion(region, scale) });
            }}
            regionsList={rescaledRegions}
          />
        </div>

        <RegionControlPanel
          degree={rotationDeg}
          drawingStatus={drawingStatus}
          mediaSize={mediaOriginSize}
          onChangeRegion={onChangeRegion}
          onChangeRotationDeg={onChangeRotationDeg}
          regionData={activeRegion}
        />
      </div>
    </div>
  );
}
